import React, { useState, useMemo } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { ChevronDown, ChevronUp, BookOpen, Info } from "lucide-react";
import {
  SECTION_META, NUTRIENT_LABELS, flattenNutrients,
  formatNutrient, percentRdi, pctColorClass,
} from "@/i18n/nutrientLabels";

// Module-level constant: сортирано веднъж, преизползвано при всички renders.
const SORTED_SECTIONS = Object.entries(SECTION_META).sort((a, b) => a[1].order - b[1].order);

/**
 * FoodDetailPanel — Cronometer-style full nutrient breakdown.
 *
 * Props:
 *   nutrients: { macros, minerals, vitamins, fats, amino } (structured USDA output)
 *   amount_g: number — current serving to scale values
 *   source: string — provenance label ("usda_db", "local", "custom"...)
 *   showPriorityOnly (default false) — hide priority-3 items
 */
export default function FoodDetailPanel({ nutrients, amount_g = 100, source = "", showPriorityOnly: initShowPriorityOnly = false }) {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [openSections, setOpenSections] = useState({ macros: true, minerals: true, vitamins: true, fats: false, amino: false });
  const [showPriorityOnly, setShowPriorityOnly] = useState(initShowPriorityOnly);

  const rows = useMemo(() => flattenNutrients(nutrients, language), [nutrients, language]);

  const grouped = useMemo(() => {
    const out = { macros: [], minerals: [], vitamins: [], fats: [], amino: [] };
    for (const r of rows) {
      if (showPriorityOnly && r.priority > 2) continue;
      out[r.section]?.push(r);
    }
    return out;
  }, [rows, showPriorityOnly]);

  const totalFields = rows.length;
  const mult = (Number(amount_g) || 100) / 100;

  if (!nutrients || totalFields === 0) {
    return (
      <div className={`p-3 rounded-lg text-[11px] flex items-start gap-1.5 ${isDark ? "bg-[hsl(223,47%,10%)] text-gray-500" : "bg-gray-50 text-gray-500"}`}
        data-testid="food-detail-no-data">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>
          {bg
            ? "Подробни нутриенти не са налични за тази храна. Използвайте USDA или NTR храни за пълен профил."
            : "Detailed nutrients not available for this food. Use USDA or NTR foods for full profile."}
        </span>
      </div>
    );
  }

  const toggleSection = (key) => setOpenSections((p) => ({ ...p, [key]: !p[key] }));

  return (
    <div className="space-y-2" data-testid="food-detail-panel">
      {/* Header controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <BookOpen className="w-3.5 h-3.5 text-[#6366F1]" />
          <p className={`text-[11px] font-bold ${isDark ? "text-gray-200" : "text-gray-800"}`}>
            {bg ? "Пълен нутриентен профил" : "Full nutrient profile"}
            <span className={`ml-1.5 text-[9px] font-normal ${isDark ? "text-gray-500" : "text-gray-400"}`}>
              {totalFields} / ~80 {bg ? "показателя" : "nutrients"}
            </span>
          </p>
        </div>
        <button
          onClick={() => setShowPriorityOnly((v) => !v)}
          className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${
            showPriorityOnly
              ? "bg-[#6366F1] text-white"
              : isDark ? "bg-[hsl(223,47%,14%)] text-gray-400" : "bg-gray-100 text-gray-500"
          }`}
          data-testid="toggle-priority-only"
        >
          {showPriorityOnly ? (bg ? "Покажи всички" : "Show all") : (bg ? "Само ключови" : "Key only")}
        </button>
      </div>

      {/* USDA source caption: explain why totalFields varies per food */}
      <p className={`text-[9.5px] ${isDark ? "text-gray-500" : "text-gray-400"}`} data-testid="usda-source-note">
        {bg
          ? `Данните са от ${source || "USDA"}. Броят нутриенти варира — USDA попълва само лабораторно тестваните за всяка храна (типично 15–70 от общо ~80). За пълни профили търсете Foundation храни (напр. броколи, сьомга).`
          : `Data from ${source || "USDA"}. Nutrient count varies — USDA only populates lab-tested values per food (typically 15–70 of ~80). For fuller profiles look up Foundation foods (e.g. broccoli, salmon).`}
      </p>

      {/* Sections */}
      {SORTED_SECTIONS.map(([key, meta]) => {
          const items = grouped[key] || [];
          if (items.length === 0) return null;
          const isOpen = !!openSections[key];
          return (
            <div key={key} className={`rounded-lg overflow-hidden ${isDark ? "bg-[hsl(223,47%,10%)]" : "bg-gray-50"}`}>
              <button
                onClick={() => toggleSection(key)}
                className={`w-full px-2.5 py-1.5 flex items-center justify-between text-left ${isDark ? "hover:bg-[hsl(223,47%,14%)]" : "hover:bg-gray-100"}`}
                data-testid={`food-detail-section-${key}`}
              >
                <div className="flex items-center gap-1.5">
                  <span className="w-1 h-4 rounded" style={{ background: meta.color }} />
                  <p className={`text-xs font-semibold ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                    {bg ? meta.bg : meta.en}
                  </p>
                  <span className={`text-[9px] font-medium ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                    {items.length}
                  </span>
                </div>
                {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
              </button>

              {isOpen && (
                <div className="px-2.5 pb-2 space-y-0.5">
                  {items.map((r) => {
                    const scaled = (Number(r.value_per100g) || 0) * mult;
                    const pct = percentRdi(scaled, r.rdi);
                    const cls = pctColorClass(pct, isDark);
                    return (
                      <div
                        key={r.key}
                        className={`flex items-center gap-2 py-1 px-1 rounded text-[11px] ${isDark ? "hover:bg-[hsl(223,47%,14%)]" : "hover:bg-white"}`}
                        data-testid={`food-detail-row-${r.key}`}
                      >
                        <p className={`flex-1 min-w-0 truncate ${isDark ? "text-gray-300" : "text-gray-700"}`} title={r.label}>
                          {r.label}
                          {r.essential && (
                            <span className="ml-1 text-[8px] font-bold text-[#EC4899]" title={bg ? "Незаменима аминокиселина" : "Essential amino acid"}>
                              *
                            </span>
                          )}
                        </p>
                        <p className={`font-mono text-right flex-shrink-0 min-w-[60px] ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                          {formatNutrient(scaled, r.unit)}
                        </p>
                        <span
                          className={`text-[9px] font-semibold px-1 py-0.5 rounded flex-shrink-0 min-w-[36px] text-center ${cls}`}
                          title={r.rdi ? (bg ? `% от дневна нужда (${r.rdi}${r.unit})` : `% of daily value (${r.rdi}${r.unit})`) : ""}
                        >
                          {pct != null ? `${pct}%` : "—"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

      {/* Footer attribution */}
      <p className={`text-[9px] italic pt-1 ${isDark ? "text-gray-600" : "text-gray-400"}`}>
        {bg
          ? `Стойности на ${amount_g}g · % RDI спрямо 2000 kcal диета (US FDA Daily Values)`
          : `Values per ${amount_g}g · %DV based on 2000 kcal diet (US FDA Daily Values)`}
      </p>
    </div>
  );
}
